/**
 * Items Page (Settings)
 * List, create, edit, and delete the tenant's price list items (services,
 * labor, products, fees) that jobs and invoices bill from.
 *
 * Each item posts revenue to a REVENUE account. PRODUCT items also need an
 * EXPENSE account; when they have a unit cost too, invoicing them posts cost
 * of goods sold (debit the expense account, credit Inventory).
 * ADMIN and OFFICE_MANAGER may change items (backend
 * requireRole('ADMIN', 'OFFICE_MANAGER')); other roles get a read-only table.
 * Delete is a soft delete; the backend refuses it while scheduled jobs use the item.
 * An item may link to a price chart: job lines for it are then priced from
 * the chart at the property's size, and unitPrice is the fallback used when
 * no chart is linked.
 * An item may also link one active checklist template (tracker item 44): adding
 * the item to a job copies the template's steps onto the job. The backend
 * rejects an inactive template (422 CHECKLIST_TEMPLATE_INACTIVE).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { createAPIClient, IChecklistTemplatesApi, IDispatchApi, IItemsApi, IGLAccountsApi, IPriceChartsApi } from '../../api';
import * as types from '../../api/types';
import { getErrorCode, getErrorMessage } from '../../utils/apiError';
import { canManageItems, canReadChecklistTemplates } from '../../utils/permissions';
import { selectableChecklistTemplates } from '../../utils/checklistTemplate';
import { sortByAccountNumber } from '../../utils/glAccount';
import { selectableCharts } from '../../utils/priceChart';
import {
  EMPTY_ITEM_FORM,
  ITEM_NAME_MAX_LENGTH,
  ITEM_TYPE_LABELS,
  ITEM_TYPE_OPTIONS,
  ItemFormState,
  buildCreateItemRequest,
  buildUpdateItemRequest,
  fetchAllPages,
  formatMoney,
  formatQuantity,
  isItemExpenseAccount,
  isItemRevenueAccount,
  itemFormFromItem,
  allowsDuration,
  allowsPortalRequest,
  postsCogs,
  requiresExpenseAccount,
  validateItemForm,
} from '../../utils/item';
import {
  inputClass,
  labelClass,
  primaryButtonClass,
  secondaryButtonClass,
  rowActionClass,
  rowDangerActionClass,
} from '../../components/formStyles';
import { StarterMark, useStarterRecords } from '../../components/StarterBadge';
import { Toast } from '../../components/Toast';

type Editing = { mode: 'create' } | { mode: 'edit'; item: types.Item } | null;

const thClass = 'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';

export function ItemsPage() {
  const { user } = useAuth();
  const apiClient = React.useMemo<IItemsApi & IGLAccountsApi & IPriceChartsApi & IDispatchApi & IChecklistTemplatesApi>(() => createAPIClient(), []);
  const canManage = canManageItems(user);
  const starter = useStarterRecords('ITEM');
  // CUSTOMER_PORTAL / OFFICE get 403 on GET /checklist-templates, so they skip it.
  const canReadTemplates = canReadChecklistTemplates(user);

  // State
  const [items, setItems] = useState<types.Item[]>([]);
  const [priceCharts, setPriceCharts] = useState<types.PriceChart[]>([]);
  const [priceChartsError, setPriceChartsError] = useState<string | null>(null);
  const [dispatchRoutes, setDispatchRoutes] = useState<types.DispatchRoute[]>([]);
  const [dispatchRoutesError, setDispatchRoutesError] = useState<string | null>(null);
  const [checklistTemplates, setChecklistTemplates] = useState<types.ChecklistTemplate[]>([]);
  const [checklistTemplatesError, setChecklistTemplatesError] = useState<string | null>(null);
  const [glAccounts, setGLAccounts] = useState<types.GLAccount[]>([]);
  const [glAccountsError, setGLAccountsError] = useState<string | null>(null);
  const [categories, setCategories] = useState<types.ItemCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<Editing>(null);
  const [form, setForm] = useState<ItemFormState>(EMPTY_ITEM_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadItems = useCallback(
    () => fetchAllPages((page, limit) => apiClient.listItems(page, limit)),
    [apiClient]
  );

  const refreshItems = useCallback(async () => {
    setItems(await loadItems());
  }, [loadItems]);

  /** All templates (active and inactive): active ones feed the picker, all of them name the linked template. */
  const refreshChecklistTemplates = useCallback(async (): Promise<types.ChecklistTemplate[] | null> => {
    try {
      const rows = await apiClient.listChecklistTemplates();
      setChecklistTemplates(rows);
      setChecklistTemplatesError(null);
      return rows;
    } catch (err) {
      setChecklistTemplates([]);
      setChecklistTemplatesError(getErrorMessage(err, 'Failed to load checklist templates'));
      return null;
    }
  }, [apiClient]);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        setGLAccountsError(null);
        setPriceChartsError(null);
        setDispatchRoutesError(null);
        setChecklistTemplatesError(null);

        // GL accounts and categories only feed the create/edit form, so only
        // managers need them, and a failure there should not hide the table.
        // Price charts also name the chart on each row, so everyone loads them.
        // Checklist templates name the linked template on each row and feed the
        // picker; a failure only hides those, never the table.
        const [itemsResult, accountsResult, categoriesResult, chartsResult, dispatchRoutesResult, templatesResult] = await Promise.allSettled([
          loadItems(),
          canManage ? apiClient.listGLAccounts() : Promise.resolve<types.GLAccount[]>([]),
          canManage
            ? fetchAllPages((page, limit) => apiClient.listItemCategories(page, limit))
            : Promise.resolve<types.ItemCategory[]>([]),
          apiClient.listPriceCharts(),
          canManage ? apiClient.listDispatchRoutes() : Promise.resolve<types.DispatchRoute[]>([]),
          canReadTemplates ? apiClient.listChecklistTemplates() : Promise.resolve<types.ChecklistTemplate[]>([]),
        ]);

        if (itemsResult.status === 'rejected') {
          throw itemsResult.reason;
        }
        setItems(itemsResult.value);

        if (accountsResult.status === 'fulfilled') {
          setGLAccounts(accountsResult.value);
        } else {
          setGLAccounts([]);
          setGLAccountsError(getErrorMessage(accountsResult.reason, 'Failed to load GL accounts'));
        }

        // Categories are optional; without them the picker just shows "None".
        setCategories(categoriesResult.status === 'fulfilled' ? categoriesResult.value : []);

        if (chartsResult.status === 'fulfilled') {
          setPriceCharts(chartsResult.value);
        } else {
          setPriceCharts([]);
          setPriceChartsError(getErrorMessage(chartsResult.reason, 'Failed to load price charts'));
        }

        if (canManage && dispatchRoutesResult.status === 'fulfilled') {
          setDispatchRoutes(dispatchRoutesResult.value);
        } else if (canManage && dispatchRoutesResult.status === 'rejected') {
          setDispatchRoutes([]);
          setDispatchRoutesError(getErrorMessage(dispatchRoutesResult.reason, 'Failed to load dispatch routes'));
        }

        if (templatesResult.status === 'fulfilled') {
          setChecklistTemplates(templatesResult.value);
        } else {
          setChecklistTemplates([]);
          setChecklistTemplatesError(getErrorMessage(templatesResult.reason, 'Failed to load checklist templates'));
        }
      } catch (err) {
        setError(getErrorMessage(err, 'Failed to load items'));
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [apiClient, canManage, canReadTemplates, loadItems]);

  // Keep the item's current accounts selectable even if they no longer qualify
  // (e.g. made non-postable), so opening the form does not silently change them.
  const editingItem = editing?.mode === 'edit' ? editing.item : null;
  const revenueAccounts = sortByAccountNumber(
    glAccounts.filter((a) => isItemRevenueAccount(a) || a.id === editingItem?.revenueAccount.id)
  );
  const expenseAccounts = sortByAccountNumber(
    glAccounts.filter((a) => isItemExpenseAccount(a) || a.id === editingItem?.expenseAccount?.id)
  );
  const sortedCategories = [...categories].sort((a, b) => a.name.localeCompare(b.name));
  const chartOptions = selectableCharts(priceCharts, editingItem?.priceChartId);
  const routeOptions = dispatchRoutes.filter((route) => route.isActive !== false);
  const chartsById = new Map(priceCharts.map((chart) => [chart.id, chart]));
  const templateOptions = selectableChecklistTemplates(checklistTemplates, editingItem?.checklistTemplateId);
  const templatesById = new Map(checklistTemplates.map((template) => [template.id, template]));

  const isProduct = form.itemType === types.ItemType.PRODUCT;
  const expenseRequired = requiresExpenseAccount(form.itemType);

  const setField = <K extends keyof ItemFormState>(field: K, value: ItemFormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const openCreate = () => {
    setEditing({ mode: 'create' });
    setForm({
      ...EMPTY_ITEM_FORM,
      // Preselect when there is exactly one valid account.
      revenueAccountId: revenueAccounts.length === 1 ? revenueAccounts[0].id : '',
    });
    setFormError(null);
    setConfirmDeleteId(null);
  };

  const openEdit = (item: types.Item) => {
    setEditing({ mode: 'edit', item });
    setForm(itemFormFromItem(item));
    setFormError(null);
    setConfirmDeleteId(null);
  };

  const closeForm = () => {
    setEditing(null);
    setForm(EMPTY_ITEM_FORM);
    setFormError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;

    const validationError = validateItemForm(form);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    try {
      setIsSaving(true);
      setFormError(null);

      if (editing.mode === 'edit') {
        const request = buildUpdateItemRequest(form, editing.item);
        if (Object.keys(request).length > 0) {
          await apiClient.updateItem(editing.item.id, request);
        }
      } else {
        await apiClient.createItem(buildCreateItemRequest(form));
      }

      closeForm();
      await refreshItems();
    } catch (err) {
      // e.g. 409 "Item name already exists in tenant" or
      // 409 "Cannot change GL accounts for item in use"
      const code = getErrorCode(err);
      // Fall back to the saved link (none on create) so the select matches the form state.
      const savedTemplateId = editing.mode === 'edit' ? editing.item.checklistTemplateId ?? '' : '';
      if (code === 'CHECKLIST_TEMPLATE_INACTIVE') {
        // Deactivated since the list loaded: reload so the picker drops it.
        setFormError('That checklist template was deactivated. Choose another template or none.');
        setField('checklistTemplateId', savedTemplateId);
        await refreshChecklistTemplates();
      } else {
        // A 404 here can be a checklist template deleted since the list loaded.
        const chosenId = form.checklistTemplateId;
        if (code === 'NOT_FOUND' && chosenId) {
          const rows = await refreshChecklistTemplates();
          if (rows && !rows.some((t) => t.id === chosenId)) setField('checklistTemplateId', savedTemplateId);
        }
        setFormError(getErrorMessage(err, editing.mode === 'edit' ? 'Failed to update item' : 'Failed to create item'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (item: types.Item) => {
    try {
      setDeletingId(item.id);
      setError(null);

      await apiClient.deleteItem(item.id);
      setConfirmDeleteId(null);
      if (editing?.mode === 'edit' && editing.item.id === item.id) closeForm();
      await refreshItems();
    } catch (err) {
      // e.g. 409 "Cannot delete item with active jobs"
      setConfirmDeleteId(null);
      setError(getErrorMessage(err, 'Failed to delete item'));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Title */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
        <div>
          <Link to="/settings" className="text-sm font-semibold text-gray-500 hover:text-blue-600">
            ← Settings
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">Items</h1>
        </div>
        {canManage && !editing && (
          <button onClick={openCreate} className={`${primaryButtonClass} w-full sm:w-auto`}>
            Add Item
          </button>
        )}
      </div>

      {error && <Toast kind="error">{error}</Toast>}

      {/* Add / Edit Form */}
      {canManage && editing && (
        <section className="bg-white rounded-lg shadow mb-6">
          <form onSubmit={handleSubmit} noValidate>
            <div className="p-4 sm:p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">
                {editing.mode === 'edit' ? `Edit ${editing.item.name}` : 'New Item'}
              </h2>

              {formError && <Toast kind="error">{formError}</Toast>}

              {glAccountsError && <Toast kind="error">{glAccountsError}</Toast>}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
                <div className="sm:col-span-3">
                  <label htmlFor="itemName" className={labelClass}>
                    Name<span className="ml-0.5 text-red-600">*</span>
                  </label>
                  <input
                    id="itemName"
                    type="text"
                    value={form.name}
                    onChange={(e) => setField('name', e.target.value)}
                    disabled={isSaving}
                    maxLength={ITEM_NAME_MAX_LENGTH}
                    placeholder="Office Cleaning"
                    className={inputClass}
                  />
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="itemType" className={labelClass}>
                    Type<span className="ml-0.5 text-red-600">*</span>
                  </label>
                  <select
                    id="itemType"
                    value={form.itemType}
                    onChange={(e) => setField('itemType', e.target.value as types.ItemType)}
                    disabled={isSaving}
                    className={inputClass}
                  >
                    {ITEM_TYPE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {ITEM_TYPE_LABELS[option]}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-6">
                  <label htmlFor="itemDescription" className={labelClass}>
                    Description
                  </label>
                  <input
                    id="itemDescription"
                    type="text"
                    value={form.description}
                    onChange={(e) => setField('description', e.target.value)}
                    disabled={isSaving}
                    placeholder="Optional"
                    className={inputClass}
                  />
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="itemUnitPrice" className={labelClass}>
                    Unit price<span className="ml-0.5 text-red-600">*</span>
                  </label>
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-500">
                      $
                    </span>
                    <input
                      id="itemUnitPrice"
                      type="text"
                      inputMode="decimal"
                      value={form.unitPrice}
                      onChange={(e) => setField('unitPrice', e.target.value)}
                      disabled={isSaving}
                      placeholder="150.00"
                      className={`${inputClass} pl-7`}
                    />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="itemUnitCost" className={labelClass}>
                    Unit cost
                  </label>
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-500">
                      $
                    </span>
                    <input
                      id="itemUnitCost"
                      type="text"
                      inputMode="decimal"
                      value={form.unitCost}
                      onChange={(e) => setField('unitCost', e.target.value)}
                      disabled={isSaving}
                      placeholder="Optional"
                      aria-describedby="itemUnitCostHint"
                      className={`${inputClass} pl-7`}
                    />
                  </div>
                  <p id="itemUnitCostHint" className="mt-1.5 text-xs text-gray-500">
                    Drives cost of goods sold for Product items with an expense account. Leave blank for no cost.
                  </p>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="itemUnitOfMeasure" className={labelClass}>
                    Unit of measure
                  </label>
                  <input
                    id="itemUnitOfMeasure"
                    type="text"
                    value={form.unitOfMeasure}
                    onChange={(e) => setField('unitOfMeasure', e.target.value)}
                    disabled={isSaving}
                    placeholder="each"
                    className={inputClass}
                  />
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="itemRevenueAccount" className={labelClass}>
                    Revenue account<span className="ml-0.5 text-red-600">*</span>
                  </label>
                  <select
                    id="itemRevenueAccount"
                    value={form.revenueAccountId}
                    onChange={(e) => setField('revenueAccountId', e.target.value)}
                    disabled={isSaving}
                    className={inputClass}
                  >
                    <option value="">Select an account</option>
                    {revenueAccounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.accountNumber} · {account.accountName}
                      </option>
                    ))}
                  </select>
                  {!glAccountsError && revenueAccounts.length === 0 && (
                    <p className="mt-1.5 text-xs text-gray-500">
                      No postable revenue accounts found. Set up the chart of accounts first.
                    </p>
                  )}
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="itemExpenseAccount" className={labelClass}>
                    Expense account
                    {expenseRequired && <span className="ml-0.5 text-red-600">*</span>}
                  </label>
                  <select
                    id="itemExpenseAccount"
                    value={form.expenseAccountId}
                    onChange={(e) => setField('expenseAccountId', e.target.value)}
                    disabled={isSaving}
                    className={inputClass}
                  >
                    <option value="">{expenseRequired ? 'Select an account' : 'None'}</option>
                    {expenseAccounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.accountNumber} · {account.accountName}
                      </option>
                    ))}
                  </select>
                  {!glAccountsError && expenseAccounts.length === 0 && (
                    <p className="mt-1.5 text-xs text-gray-500">
                      No postable expense accounts found. Set up the chart of accounts first.
                    </p>
                  )}
                  {isProduct && (
                    <p className="mt-1.5 text-xs text-gray-500">
                      {postsCogs(form)
                        ? 'Invoicing this product posts cost of goods sold to this account (unit cost × quantity).'
                        : 'Cost of goods sold only posts when both a unit cost and an expense account are set.'}
                    </p>
                  )}
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="itemCategory" className={labelClass}>
                    Category
                  </label>
                  <select
                    id="itemCategory"
                    value={form.categoryId}
                    onChange={(e) => setField('categoryId', e.target.value)}
                    disabled={isSaving}
                    className={inputClass}
                  >
                    <option value="">None</option>
                    {sortedCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                    {/* Keep the current category selectable if it did not load. */}
                    {editingItem?.category &&
                      !sortedCategories.some((c) => c.id === editingItem.category?.id) && (
                        <option value={editingItem.category.id}>{editingItem.category.name}</option>
                      )}
                  </select>
                </div>

                <div className="sm:col-span-3 flex items-end">
                  <label htmlFor="itemTaxable" className="inline-flex items-center gap-2 py-2.5 text-sm text-gray-700">
                    <input
                      id="itemTaxable"
                      type="checkbox"
                      checked={form.taxable}
                      onChange={(e) => setField('taxable', e.target.checked)}
                      disabled={isSaving}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="font-medium">Taxable</span>
                  </label>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="itemPriceChart" className={labelClass}>
                    Price chart
                  </label>
                  <select
                    id="itemPriceChart"
                    value={form.priceChartId}
                    onChange={(e) => setField('priceChartId', e.target.value)}
                    disabled={isSaving}
                    aria-describedby="itemPriceChartHint"
                    className={inputClass}
                  >
                    <option value="">None (use the unit price)</option>
                    {chartOptions.map((chart) => (
                      <option key={chart.id} value={chart.id}>
                        {chart.name}
                        {chart.active ? '' : ' (inactive)'}
                      </option>
                    ))}
                    {/* Keep the current chart selectable if the chart list did not load. */}
                    {editingItem?.priceChartId && !chartOptions.some((c) => c.id === editingItem.priceChartId) && (
                      <option value={editingItem.priceChartId}>Current chart</option>
                    )}
                  </select>
                  {priceChartsError && <Toast kind="error">{priceChartsError}</Toast>}
                </div>

                <div className="sm:col-span-3 flex items-end">
                  <p id="itemPriceChartHint" className="text-xs text-gray-500 pb-1">
                    With a chart, job lines are priced from it using the property's size (a property with no size
                    needs a manual price). The unit price stays the item's default and is used whenever no chart is
                    linked.{' '}
                    <Link to="/settings/price-charts" className="font-semibold text-blue-600 hover:text-blue-700">
                      Manage price charts
                    </Link>
                  </p>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="itemRoute" className={labelClass}>Default route</label>
                  <select
                    id="itemRoute"
                    value={form.routeId}
                    onChange={(e) => setField('routeId', e.target.value)}
                    disabled={isSaving || !!dispatchRoutesError}
                    className={inputClass}
                  >
                    <option value="">None</option>
                    {routeOptions.map((route) => <option key={route.id} value={route.id}>{route.name}</option>)}
                    {editingItem?.routeId && !routeOptions.some((route) => route.id === editingItem.routeId) && (
                      <option value={editingItem.routeId}>Current route</option>
                    )}
                  </select>
                  {dispatchRoutesError
                    ? <p className="mt-1.5 text-xs text-red-600">{dispatchRoutesError}</p>
                    : <p className="mt-1.5 text-xs text-gray-500">Jobs created from this item can use this route by default.</p>}
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="itemChecklistTemplate" className={labelClass}>Checklist template</label>
                  <select
                    id="itemChecklistTemplate"
                    value={form.checklistTemplateId}
                    onChange={(e) => setField('checklistTemplateId', e.target.value)}
                    disabled={isSaving || !!checklistTemplatesError}
                    aria-describedby="itemChecklistTemplateHint"
                    className={inputClass}
                  >
                    <option value="">None</option>
                    {templateOptions.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                        {template.isActive ? '' : ' (inactive)'}
                      </option>
                    ))}
                    {/* Keep the current template selectable if the template list did not load. */}
                    {editingItem?.checklistTemplateId && !templateOptions.some((t) => t.id === editingItem.checklistTemplateId) && (
                      <option value={editingItem.checklistTemplateId}>Current template</option>
                    )}
                  </select>
                  {checklistTemplatesError ? (
                    <p className="mt-1.5 text-xs text-red-600">{checklistTemplatesError}</p>
                  ) : (
                    <p id="itemChecklistTemplateHint" className="mt-1.5 text-xs text-gray-500">
                      Adding this item to a job copies the template's steps onto the job.{' '}
                      <Link to="/settings/checklist-templates" className="font-semibold text-blue-600 hover:text-blue-700">
                        Manage checklist templates
                      </Link>
                    </p>
                  )}
                </div>

                {allowsDuration(form.itemType) && (
                  <div className="sm:col-span-2">
                    <label htmlFor="itemDuration" className={labelClass}>
                      Typical duration per unit (minutes)
                    </label>
                    <input
                      id="itemDuration"
                      type="number"
                      min={5}
                      max={1440}
                      step={5}
                      value={form.defaultDurationMinutes}
                      onChange={(e) => setField('defaultDurationMinutes', e.target.value)}
                      disabled={isSaving}
                      placeholder="Optional"
                      aria-describedby="itemDurationHint"
                      className={inputClass}
                    />
                  </div>
                )}
                {allowsDuration(form.itemType) && (
                  <div className="sm:col-span-4 flex items-end">
                    <p id="itemDurationHint" className="text-xs text-gray-500 pb-1">
                      A job's suggested duration adds this up over its work items (minutes x quantity, rounded up to
                      5 minutes). Hourly lines count their quantity as hours. Leave blank if the item takes no time.
                    </p>
                  </div>
                )}

                {allowsPortalRequest(form.itemType) && (
                  <div className="sm:col-span-6">
                    <label className="flex min-h-[44px] items-start gap-3 text-sm text-gray-900">
                      <input
                        id="itemPortalRequestable"
                        type="checkbox"
                        className="mt-1 h-5 w-5"
                        checked={form.portalRequestable}
                        onChange={(e) => setField('portalRequestable', e.target.checked)}
                        disabled={isSaving}
                        aria-describedby="itemPortalRequestableHint"
                      />
                      <span>
                        <span className="block font-medium">Offer on the customer portal</span>
                        <span id="itemPortalRequestableHint" className="block text-xs font-normal text-gray-500">
                          Customers can pick this service on the portal's Request service form. It only starts a request: you decide whether to schedule it.
                        </span>
                      </span>
                    </label>
                  </div>
                )}

                {isProduct && (
                  <div className="sm:col-span-2">
                    <label htmlFor="itemReorderPoint" className={labelClass}>
                      Reorder point
                    </label>
                    <input
                      id="itemReorderPoint"
                      type="text"
                      inputMode="decimal"
                      value={form.reorderPoint}
                      onChange={(e) => setField('reorderPoint', e.target.value)}
                      disabled={isSaving}
                      placeholder="Optional"
                      aria-describedby="itemReorderPointHint"
                      className={inputClass}
                    />
                  </div>
                )}
                {isProduct && (
                  <div className="sm:col-span-4 flex items-end">
                    <p id="itemReorderPointHint" className="text-xs text-gray-500 pb-1">
                      The product shows as low stock on the Inventory page when the quantity on hand is at or below
                      this number. Leave blank for no low-stock warning.
                    </p>
                  </div>
                )}

                {editingItem && (
                  <div className="sm:col-span-6">
                    <p className="text-sm text-gray-600">
                      Quantity on hand:{' '}
                      <span className="font-semibold text-gray-900">{formatQuantity(editingItem.quantityOnHand)}</span>
                      {editingItem.lowStock && <span className="ml-2 text-amber-700">(low stock)</span>}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end rounded-b-lg border-t border-gray-200 bg-gray-50 px-4 sm:px-6 py-4">
              <button type="button" onClick={closeForm} disabled={isSaving} className={`${secondaryButtonClass} w-full sm:w-auto`}>
                Cancel
              </button>
              <button type="submit" disabled={isSaving} className={`${primaryButtonClass} w-full sm:w-auto`}>
                {isSaving ? 'Saving...' : editing.mode === 'edit' ? 'Save Changes' : 'Create Item'}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="bg-white rounded-lg shadow p-4 sm:p-6">
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        )}

        {/* Table */}
        {!isLoading &&
          (items.length === 0 ? (
            <p className="text-gray-600 text-center py-8">No items yet</p>
          ) : (
            <div className="md:overflow-x-auto">
              <table className="responsive-table min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={thClass}>Name</th>
                    <th className={thClass}>Type</th>
                    <th className={thClass}>Price</th>
                    <th className={thClass}>Cost</th>
                    <th className={thClass}>Revenue Account</th>
                    <th className={thClass}>Expense Account</th>
                    <th className={thClass}>Category</th>
                    <th className={thClass}>On Hand</th>
                    {canManage && (
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <span className="sr-only">Actions</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((item) => {
                    const isConfirming = confirmDeleteId === item.id;
                    const isDeleting = deletingId === item.id;
                    return (
                      <tr key={item.id}>
                        <td data-label="Name" data-primary className="px-4 py-3">
                          <div className="min-w-0 break-words">
                            <div className="font-semibold text-gray-900">{item.name}</div>
                            <StarterMark records={starter} entityId={item.id} name={item.name} />
                            {item.description && <div className="text-sm font-normal text-gray-500">{item.description}</div>}
                            {!item.taxable && <div className="text-xs font-normal text-gray-500">Non-taxable</div>}
                            {item.portalRequestable && <div className="text-xs font-normal text-blue-700">Offered on the customer portal</div>}
                            {item.checklistTemplateId && (
                              <div className="text-xs font-normal text-blue-700">
                                Checklist: {templatesById.get(item.checklistTemplateId)?.name ?? 'linked'}
                                {templatesById.get(item.checklistTemplateId)?.isActive === false && (
                                  <span className="text-amber-700"> (inactive)</span>
                                )}
                              </div>
                            )}
                            {item.isSystem && <div className="text-xs font-normal text-gray-500">System item (used on finance-charge invoices; cannot be edited or deleted)</div>}
                          </div>
                        </td>
                        <td data-label="Type" className="px-4 py-3 text-sm text-gray-900">{ITEM_TYPE_LABELS[item.itemType] ?? item.itemType}{item.defaultDurationMinutes ? <div className="text-xs text-gray-500">{item.defaultDurationMinutes} min per unit</div> : null}</td>
                        <td data-label="Price" className="px-4 py-3 text-sm text-gray-900 md:whitespace-nowrap">
                          <div className="text-right md:text-left">
                            {formatMoney(item.unitPrice)}
                            <span className="text-gray-500"> / {item.unitOfMeasure}</span>
                            {item.priceChartId && (
                              <div className="text-xs text-blue-700 break-words">
                                Chart: {chartsById.get(item.priceChartId)?.name ?? 'linked'}
                                {chartsById.get(item.priceChartId)?.active === false && (
                                  <span className="text-amber-700"> (inactive)</span>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td data-label="Cost" className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {item.unitCost !== null ? formatMoney(item.unitCost) : '—'}
                        </td>
                        <td data-label="Revenue Account" className="px-4 py-3 text-sm text-gray-600 break-words">
                          {item.revenueAccount.accountNumber} · {item.revenueAccount.accountName}
                        </td>
                        <td data-label="Expense Account" className="px-4 py-3 text-sm text-gray-600 break-words">
                          {item.expenseAccount
                            ? `${item.expenseAccount.accountNumber} · ${item.expenseAccount.accountName}`
                            : '—'}
                        </td>
                        <td data-label="Category" className="px-4 py-3 text-sm text-gray-600">{item.category?.name ?? '—'}</td>
                        <td data-label="On Hand" className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          <div className="text-right md:text-left">
                            {formatQuantity(item.quantityOnHand)}
                            {item.lowStock && (
                              <div className="text-xs text-amber-700" title={`At or below the reorder point (${formatQuantity(item.reorderPoint ?? '')})`}>
                                Low stock
                              </div>
                            )}
                          </div>
                        </td>
                        {canManage && (
                          <td className="px-4 py-3 text-right md:whitespace-nowrap">
                            {item.isSystem ? (
                              <span className="text-xs text-gray-400">System</span>
                            ) : isConfirming ? (
                              <span className="inline-flex flex-wrap items-center gap-3">
                                <span className="text-sm text-gray-700">Delete this item?</span>
                                <button
                                  onClick={() => handleDelete(item)}
                                  disabled={isDeleting}
                                  className={`${rowDangerActionClass} min-h-[44px] md:min-h-0`}
                                >
                                  {isDeleting ? 'Deleting...' : 'Confirm'}
                                </button>
                                <button
                                  onClick={() => setConfirmDeleteId(null)}
                                  disabled={isDeleting}
                                  className={`${rowActionClass} min-h-[44px] md:min-h-0`}
                                >
                                  Cancel
                                </button>
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-4">
                                <button onClick={() => openEdit(item)} disabled={isSaving} className={`${rowActionClass} min-h-[44px] md:min-h-0`}>
                                  Edit
                                </button>
                                <button
                                  onClick={() => setConfirmDeleteId(item.id)}
                                  disabled={deletingId !== null}
                                  aria-label={`Delete ${item.name}`}
                                  className={`${rowDangerActionClass} min-h-[44px] md:min-h-0`}
                                >
                                  Delete
                                </button>
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
      </section>
    </main>
  );
}
